const resources = {
  en: {
    obligationsHub: {
      title: 'Obligations',
      description: 'One place for debts, credit-card payoff, liabilities, and repayment-plan evidence.',
      empty: 'No obligations yet.',
      outstanding: 'Outstanding',
      actions: {
        openSource: 'Open source',
        addProduct: 'Add obligation',
        createPlan: 'Create plan',
      },
      types: {
        personal_debt: 'Personal debt',
        credit_card: 'Credit card',
        liability: 'Liability',
        auto_loan: 'Auto loan',
        auto_lease: 'Auto lease',
        mortgage: 'Mortgage',
        cash_credit: 'Cash credit',
        custom_obligation: 'Custom obligation',
      },
      plan: {
        none: 'No plan',
        createHint: 'Create a repayment plan from the source page so future installments and evidence stay tied to the obligation.',
        createHintWithTasks: 'A formal repayment plan is not set yet. Existing tasks will still confirm as obligation payments.',
        productCreateHint: 'Create a payoff plan here so future installments become auto-generated tasks with evidence.',
        productCreateHintWithTasks: 'A formal payoff plan is not set yet. Existing tasks will still confirm as obligation payments.',
        legacyLiabilityHint: 'Legacy liability record. Use debts, credit cards, or future obligation products for new payoff plans.',
      },
      tasks: {
        badge: '{{count}} tasks',
        open: 'Open payment tasks already scheduled: {{count}}.',
        openWithOverdue: 'Open payment tasks: {{count}}, overdue: {{overdueCount}}.',
        nextDue: 'Next: {{date}}.',
      },
      productForm: {
        title: 'Add obligation product',
        type: 'Type',
        name: 'Name',
        institution: 'Institution',
        amount: 'Current amount owed',
        amountHint: 'Use a decimal string, for example 1250.00',
        currency: 'Currency',
        description: 'Notes',
        required: 'Required',
        submit: 'Add obligation',
        cancel: 'Cancel',
      },
      summary: {
        total: {
          label: 'Tracked obligations',
          supporting: 'Debts, cards, and liabilities',
        },
        planned: {
          label: 'With plans',
          supporting: 'Draft, active, or paused repayment plans',
        },
        unplanned: {
          label: 'Without plans',
          supporting: 'Need an explicit repayment promise',
        },
        attention: {
          label: 'Need attention',
          supporting: 'Overdue or late plan evidence',
        },
      },
      sections: {
        overview: {
          title: 'Repayment coverage',
          description: 'Plans are read from repayment plan evidence. Confirming a generated task is still the only action that records real ledger movement.',
        },
      },
      messages: {
        loadError: 'Unable to load obligations',
        productCreated: 'Obligation added',
        productCreateError: 'Unable to add obligation',
        planCreated: 'Repayment plan created',
        planCreateError: 'Unable to create repayment plan',
      },
    },
  },
  uk: {
    obligationsHub: {
      title: 'Зобов’язання',
      description: 'Єдине місце для боргів, погашення кредиток, зобов’язань і доказів виконання планів платежів.',
      empty: 'Зобов’язань поки немає.',
      outstanding: 'Залишок',
      actions: {
        openSource: 'Відкрити джерело',
        addProduct: 'Додати зобов’язання',
        createPlan: 'Створити план',
      },
      types: {
        personal_debt: 'Особистий борг',
        credit_card: 'Кредитна картка',
        liability: 'Зобов’язання',
        auto_loan: 'Автокредит',
        auto_lease: 'Автолізинг',
        mortgage: 'Іпотека',
        cash_credit: 'Грошовий кредит',
        custom_obligation: 'Інше зобов’язання',
      },
      plan: {
        none: 'Без плану',
        createHint: 'Створіть план платежів на сторінці-джерелі, щоб майбутні платежі й докази були прив’язані до зобов’язання.',
        createHintWithTasks: 'Формального плану платежів ще немає. Наявні задачі все одно підтверджуються як платежі за зобов’язанням.',
        productCreateHint: 'Створіть план погашення тут, щоб майбутні платежі стали автоматичними задачами з доказами.',
        productCreateHintWithTasks: 'Формального плану погашення ще немає. Наявні задачі все одно підтверджуються як платежі за зобов’язанням.',
        legacyLiabilityHint: 'Застарілий запис зобов’язання. Для нових планів погашення використовуйте борги, кредитні картки або майбутні типи зобов’язань.',
      },
      tasks: {
        badge: '{{count}} задач',
        open: 'Відкритих платіжних задач уже заплановано: {{count}}.',
        openWithOverdue: 'Відкритих платіжних задач: {{count}}, прострочено: {{overdueCount}}.',
        nextDue: 'Наступна: {{date}}.',
      },
      productForm: {
        title: 'Додати тип зобов’язання',
        type: 'Тип',
        name: 'Назва',
        institution: 'Установа',
        amount: 'Поточна сума боргу',
        amountHint: 'Використовуйте десятковий рядок, наприклад 1250.00',
        currency: 'Валюта',
        description: 'Нотатки',
        required: 'Обовʼязково',
        submit: 'Додати зобов’язання',
        cancel: 'Скасувати',
      },
      summary: {
        total: {
          label: 'Зобов’язань',
          supporting: 'Борги, картки та зобов’язання',
        },
        planned: {
          label: 'З планами',
          supporting: 'Чернетки, активні або призупинені плани',
        },
        unplanned: {
          label: 'Без планів',
          supporting: 'Потрібна явна обіцянка погашення',
        },
        attention: {
          label: 'Потребують уваги',
          supporting: 'Прострочені або запізнілі докази плану',
        },
      },
      sections: {
        overview: {
          title: 'Покриття планами',
          description: 'Плани читаються з repayment-plan evidence. Реальний рух у ledger з’являється тільки після підтвердження створеної задачі.',
        },
      },
      messages: {
        loadError: 'Не вдалося завантажити зобов’язання',
        productCreated: 'Зобов’язання додано',
        productCreateError: 'Не вдалося додати зобов’язання',
        planCreated: 'План платежів створено',
        planCreateError: 'Не вдалося створити план платежів',
      },
    },
  },
}

export default resources
